import { Box, Grid, Spinner, Typography } from '@forgeglobal/design-framework';
import useAccounts from '@tcs/client-portal-common/hooks/useAccounts';
import useSession from '@tcs/client-portal-common/hooks/useSession';
import useTransfersInRequest from '@tcs/client-portal-common/hooks/useTransfersInRequest';
import { useAnalytics } from '@tcs/common/hooks/analytics';
import { CashDeliveryMethod } from '@tcs/common/types/CashDeliveryMethod';
import { TransferMethod } from '@tcs/common/types/TransferMethod';
import { TransferType } from '@tcs/common/types/TransferType';
import formatPhone from '@tcs/common/utils/formatPhone';
import cs from 'classnames';
import { useEffect, useRef, useState } from 'react';
import BaseDialog from '../DialogLayout/BaseDialog';
import TransfersInCashForm, {
  ACCOUNT_TYPE_MAP,
  TransfersInCashFormData,
} from './forms/TransfersInCashForm';
import useStyles from './style';

const DOCUSIGN_SIGNING_COMPLETE_EVENT = 'signing_complete';

interface TransfersInCashDialogProps {
  open: boolean;
  onClose: () => void;
  onSigningResult?: (isComplete: boolean) => void;
}

const transferRequirements = [
  `The account number of the account you're transferring`,
  `The name of the financial institution`,
  `A recent account statement`,
];

const transferCallInstructions = [
  `You're rolling over from a 401(k), 403(b), or 457(b) or other workplace plan`,
  `You're transferring between retirement accounts and there is ANY difference in how your names appear on the accounts`,
  `You're looking to transfer assets (e.g., stocks, real estate), not just cash`,
];

const TransfersInCashDialog: React.FC<TransfersInCashDialogProps> = ({
  open,
  onClose,
  onSigningResult,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [docuSignUrl, setDocuSignUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { loading: loadingSession, profile, claims } = useSession();
  const { data: accounts = [] } = useAccounts();
  const [transferData, setTransferData] =
    useState<TransfersInCashFormData | null>(null);

  const { trackEvent, trackPageView } = useAnalytics();
  const {
    isLoading: loadingTransfersInRequest,
    mutate: createTransfersInRequest,
  } = useTransfersInRequest();

  useEffect(() => {
    trackPageView('TransfersInCashDialog');
  }, [trackPageView]);

  const handleCancel = () => {
    if (docuSignUrl) {
      trackEvent('button_click', 'TransfersInDocusignCanceled');
      setDocuSignUrl(null);
      return;
    }

    trackEvent('button_click', 'TransfersInCashCanceled');
    setDocuSignUrl(null);
    setTransferData(null);
    onClose();
  };

  const handleSubmit = (data: TransfersInCashFormData) => {
    setLoading(true);

    const account = accounts.find(
      (account) => account.accountNumber === data.selectedAccount,
    );

    const accountType = ACCOUNT_TYPE_MAP[account?.actualAccountType];

    const { firstName, lastName, dateOfBirth } = profile.personalInfo;
    const { email, primaryPhone } = profile.contactInfo;
    const [year, month, day] = dateOfBirth.split('-');

    const signingRequest = {
      email,
      name: `${firstName} ${lastName}`,
      clientUserId: claims.clientPortalIdentity,
      tabs: {
        textTabs: [
          { tabLabel: 'FTC-Source', value: 'ClientPortal' },
          { tabLabel: 'ISTC-1-First_Name', value: firstName },
          { tabLabel: 'ISTC-1-Last_Name', value: lastName },
          { tabLabel: 'ISTC-1-Account_Number', value: account.accountNumber },
          { tabLabel: 'ISTC-1-DOB', value: `${month}/${day}/${year}` },
          { tabLabel: 'ISTC-1-Phone', value: formatPhone(primaryPhone) },
          { tabLabel: 'ISTC-1-Email', value: email },
          { tabLabel: 'TAC-2-Account_Number', value: data.transferFromAccount },
          { tabLabel: 'TAC-2-Custodian_Name', value: data.iraProvider },
          { tabLabel: 'TAC-2-Custodian_Address', value: data.address },
          { tabLabel: 'TAC-2-Custodian_City', value: data.city },
          { tabLabel: 'TAC-2-Custodian_State', value: data.state },
          { tabLabel: 'TAC-2-Custodian_Zip', value: data.zipCode },
          { tabLabel: 'TAC-2-Contact_Phone', value: formatPhone(data.phone) },
          {
            tabLabel: 'TAC-2-FedEx_Account_Number',
            value: data.overnightDeliveryCostFedexAccountNumber || '',
          },
          {
            tabLabel: 'TAC-3-Transfer_Amount',
            value:
              data.transferType === TransferType.Partial
                ? data.transferMethod === TransferMethod.ExactAmount
                  ? Number(data.transferAmount || 0).toFixed(2)
                  : 'All Available Cash'
                : '',
          },
        ],
        checkboxTabs: [
          {
            tabLabel: 'TAC-0-Priority-CB',
            selected: data.expediteProcessing ? 'true' : 'false',
          },
          {
            tabLabel: 'TAC-1-Inherited-CB',
            selected: account.isInherited ? 'true' : 'false',
          },
          {
            tabLabel: 'TAC-2-Inherited-CB',
            selected: data.isInheritedAccount ? 'true' : 'false',
          },
          {
            tabLabel: 'TAC-2-Overnight_Transfer-CB',
            selected: data.overnightDelivery ? 'true' : 'false',
          },
        ],
        radioGroupTabs: [
          {
            groupName: 'TAC-1-IRA_Account_Type-RB',
            radios: Object.values(ACCOUNT_TYPE_MAP).map((type) => ({
              value: type,
              selected: type === accountType ? 'true' : 'false',
            })),
          },
          {
            groupName: 'TAC-2-IRA_Account_Type-RB',
            radios: Object.values(ACCOUNT_TYPE_MAP).map((type) => ({
              value: type,
              selected: type === data.accountType ? 'true' : 'false',
            })),
          },
          {
            groupName: 'TAC-3-Transfer-RB',
            radios: [
              {
                value: 'Full Cash Transfer',
                selected:
                  data.transferType === TransferType.Full ? 'true' : 'false',
              },
              {
                value: 'Partial Transfer',
                selected:
                  data.transferType === TransferType.Partial ? 'true' : 'false',
              },
            ],
          },
          {
            groupName: 'TAC-4-Delivery_Instructions-RB',
            radios: [
              {
                value: 'TAC-4-Delivery_Instructions-Mail',
                selected:
                  data.deliverCashBy === CashDeliveryMethod.Check
                    ? 'true'
                    : 'false',
              },
              {
                value: 'TAC-4-Delivery_Instructions-Wire',
                selected:
                  data.deliverCashBy === CashDeliveryMethod.Wire
                    ? 'true'
                    : 'false',
              },
            ],
          },
        ],
      },
    };

    setTransferData(data);

    createTransfersInRequest(signingRequest, {
      onSuccess: (response) => {
        setDocuSignUrl(response.url);
        trackEvent('form_submission', 'TransfersInDocusignStarted');
      },
      onError: (error) => {
        console.error('Failed to create DocuSign signing request:', error);
        trackEvent('form_submission', 'TransfersInDocusignFailed');
        setLoading(false);
      },
    });
  };

  const handleOnLoad = () => {
    setLoading(false);

    try {
      const href = iframeRef.current?.contentWindow?.location?.href;
      if (!href?.includes('funding-request-completed')) return;

      const event = new URL(href).searchParams.get('event');
      const isComplete = event === DOCUSIGN_SIGNING_COMPLETE_EVENT;
      // Clear iframe/form state so a new Transfer Cash attempt starts clean.
      setDocuSignUrl(null);
      setTransferData(null);
      onClose();
      onSigningResult?.(isComplete);
    } catch {
      // Cross-origin DocuSign iframe — expected while signing is in progress.
    }
  };

  const renderTransferRequirements = () => {
    return (
      <Box component="ul" className={classes.list} sx={{ mb: 4 }}>
        {transferRequirements.map((requirement, index) => (
          <Box
            key={`transfer-requirements-${index}`}
            component="li"
            className={classes.listItem}
            sx={{
              mb: index === transferRequirements.length - 1 ? 0 : 2,
            }}>
            <Box className={classes.listItemBullet} />
            <Typography variant="body2">{requirement}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderTransferCallInstructions = () => {
    return (
      <Box component="ul" className={classes.list}>
        {transferCallInstructions.map((instruction, index) => (
          <Box
            key={`transfer-call-instructions-${index}`}
            component="li"
            className={classes.listItem}
            sx={{
              mb: index === transferCallInstructions.length - 1 ? 0 : 2,
            }}>
            <Box className={classes.listItemBullet} />
            <Typography variant="body2">{instruction}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderLoadingSpinner = () => (
    <div className={classes.loadingSpinner}>
      <Spinner />
    </div>
  );

  if (loadingSession) {
    return null;
  }

  return (
    <BaseDialog
      open={open}
      onClose={handleCancel}
      name="transfer-in-dialog"
      showCloseArrow
      hideDisclaimer
      fixedLayout
      data-test-id="transfers-cash-dialog"
      keepMounted={false}>
      <Grid container className={classes.container}>
        <Grid item xs={12} md={4} className={classes.leftPanel}>
          <Box
            className={cs([classes.overflowPanel, classes.leftPanelContainer])}>
            <Box className={classes.infoBox}>
              <Typography variant="body2" className={classes.title}>
                Online IRA Transfer
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mb: 4,
                }}>
                Getting started is simple—you can transfer your IRA directly
                from your current IRA provider by filling out our online
                transfer form
              </Typography>

              <Typography variant="body2" className={classes.title}>
                To transfer an IRA, you&apos;ll need the following
              </Typography>

              {renderTransferRequirements()}

              <Typography
                variant="body2"
                className={classes.title}
                sx={{
                  mb: 2,
                }}>
                Call Forge Trust at 800-248-8447 if:
              </Typography>

              {renderTransferCallInstructions()}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={8} className={classes.rightPanel}>
          {(loading || loadingTransfersInRequest) && renderLoadingSpinner()}
          {!loadingTransfersInRequest && !docuSignUrl && (
            <Box
              className={cs([
                classes.overflowPanel,
                classes.rightPanelContainer,
              ])}
              data-test-id="transfer-cash-dialog-form-container">
              <TransfersInCashForm
                transferData={transferData}
                accounts={accounts}
                onCancel={handleCancel}
                onSubmit={handleSubmit}
              />
            </Box>
          )}
          {docuSignUrl && (
            <Box
              className={cs([classes.overflowPanel, classes.docuSignContainer])}
              data-test-id="transfer-cash-dialog-docusign-container">
              <iframe
                ref={iframeRef}
                src={docuSignUrl}
                onLoad={handleOnLoad}
                title="DocuSign Form"
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </BaseDialog>
  );
};

export default TransfersInCashDialog;
